/**
 * ★ Fase 94 (Doc 02) — Dialog lead "progressivo" in 3 step, riutilizzabile (Three Garden, Narea…):
 *   1) Contatti (nome / email / telefono)  → POST /api/public/funnel-lead  (evento di verità `lead_created`)
 *   2) Prequalifica (obiettivo / budget / orizzonte) PRIMA di Calendly
 *                                           → POST /api/public/funnel-lead/prequal (`prequal_completed`)
 *      con link "Preferisco rispondere in Zoom" (skip: nessun evento falso)
 *   3) Conferma + redirect Calendly con prefill nome/email + UTM preservati (`booking_redirect`)
 *
 * Nessun segreto nel frontend: l'URL Calendly arriva dal CRM (GET /public/funnel/<slug>).
 * Palette passata via `tokens` (la landing mantiene i propri colori: niente cambio brand).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowLeft, CalendarClock, CheckCircle2, Loader2, Mail, Phone, Send, User, Video,
  Target, Wallet, CalendarRange, MapPin, Check,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { api } from '../../lib/api';
import { getAttribution, trackFunnelEvent, withCalendlyPrefill, withUtm } from '../../lib/attribution';
import { PREQUAL_OPTIONS, cleanPrequal } from './PrequalFields';
import { NoObligationNote } from './NoObligationNote';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REDIRECT_MS = 3000;

const DEFAULT_TOKENS = {
  bg: '#F9F6F0', paper: '#FFFDF9', ink: '#1A2318', inkSoft: '#59615B',
  accent: '#3C5330', accentDeep: '#2F4226', line: '#E3DCCF', soft: '#EBEEE3', onAccent: '#FFFBF3',
};

function Field({ icon: Icon, testId, tokens, ...props }) {
  return (
    <div className="relative">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: tokens.accent }} strokeWidth={1.8} aria-hidden="true" />
      <input {...props} data-testid={testId} className="zld-field w-full h-12 rounded-xl pl-11 pr-4 text-sm font-sans" />
    </div>
  );
}

function Stepper({ steps, current, tokens, testIdPrefix }) {
  return (
    <ol className="flex items-center gap-2" aria-label="Avanzamento" data-testid={`${testIdPrefix}-stepper`}>
      {steps.map((label, i) => {
        const state = i < current ? 'done' : i === current ? 'current' : 'todo';
        return (
          <li key={label} className="flex items-center gap-2 min-w-0" data-testid={`${testIdPrefix}-step-${i + 1}`} data-state={state}>
            <span
              className="w-6 h-6 rounded-full grid place-items-center text-[11px] font-sans font-bold shrink-0"
              style={{
                background: state === 'todo' ? 'transparent' : tokens.accent,
                color: state === 'todo' ? tokens.inkSoft : tokens.onAccent,
                border: `1px solid ${state === 'todo' ? tokens.line : tokens.accent}`,
              }}
              aria-current={state === 'current' ? 'step' : undefined}
            >
              {state === 'done' ? <Check className="w-3.5 h-3.5" /> : i + 1}
            </span>
            <span className="text-[11px] font-sans font-semibold tracking-[0.08em] uppercase truncate" style={{ color: state === 'current' ? tokens.ink : tokens.inkSoft }}>{label}</span>
            {i < steps.length - 1 && <span className="w-5 h-px shrink-0" style={{ background: tokens.line }} aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
}

function ChipGroup({ icon: Icon, label, options, value, onChange, lang, tokens, testId }) {
  return (
    <fieldset className="min-w-0" data-testid={testId}>
      <legend className="mb-2 flex items-center gap-1.5 text-[11px] font-sans font-semibold tracking-[0.12em] uppercase" style={{ color: tokens.accent }}>
        <Icon className="w-3.5 h-3.5" aria-hidden="true" /> {label}
      </legend>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={label}>
        {options.map((o) => {
          const selected = value === o.id;
          const text = o.label || o[lang] || o.it;
          return (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(selected ? undefined : o.id)}
              className="zld-chip rounded-full px-3.5 h-9 text-[12.5px] font-sans font-medium"
              data-state={selected ? 'selected' : 'idle'}
              data-testid={`${testId}-${o.id}`}
            >
              {text}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {(v:boolean)=>void} props.onOpenChange
 * @param {string} props.bookingUrl         URL Calendly live (dal CRM)
 * @param {string} props.funnel             slug funnel (es. 'it-al-tg')
 * @param {string} [props.propertySlug]
 * @param {string} [props.leadMessage]
 * @param {'it'|'es'|'en'} [props.lang]
 * @param {boolean} [props.brochureEmail]   true se la brochure parte via email al submit
 * @param {object} props.t                  testi (vedi CONTENT.dlg della landing)
 * @param {object} [props.tokens]           palette della landing
 * @param {string} [props.testIdPrefix]
 * @param {{title:string,text:string,href?:string,cta?:string}} [props.bridge]  nota "dopo la Zoom" nello step finale
 * @param {string} [props.intent]           intent CRM (default 'zoom')
 * @param {string} [props.successHref]      se valorizzato: lo step 3 porta a questa route interna (es. hub /f/it) invece che a Calendly
 */
export function ZoomLeadDialog({
  open, onOpenChange, bookingUrl, funnel, propertySlug, leadMessage, lang = 'it', brochureEmail = false,
  t, tokens: tk, testIdPrefix = 'zld', bridge, intent = 'zoom', successHref = null,
}) {
  const tokens = { ...DEFAULT_TOKENS, ...(tk || {}) };
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 contatti · 1 prequal · 2 done
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [prequal, setPrequal] = useState({});
  const [leadId, setLeadId] = useState(null);
  const [submittedKey, setSubmittedKey] = useState(null); // evita doppio invio se l'utente torna indietro senza modifiche
  const [sending, setSending] = useState(false);
  const [touched, setTouched] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  // Chiusura del dialog → stop redirect automatico (l'utente può riaprire e usare il bottone)
  useEffect(() => { if (!open && timer.current) { clearTimeout(timer.current); timer.current = null; } }, [open]);

  const nameOk = name.trim().length >= 2;
  const emailOk = EMAIL_RE.test(email.trim());
  const valid = nameOk && emailOk;
  const pq = cleanPrequal(prequal);
  const pqComplete = !!(pq && pq.goal && pq.budget_bracket && pq.timeline);

  const internalHref = successHref ? withUtm(successHref) : null;
  const finalBookingUrl = internalHref || withCalendlyPrefill(bookingUrl, { name: name.trim(), email: email.trim(), leadId, funnel });

  const goBooking = (position) => {
    trackFunnelEvent('booking_redirect', { funnel, lead_id: leadId, target: internalHref ? 'hub' : 'calendly', position, beacon: !internalHref });
    if (internalHref) { onOpenChange?.(false); navigate(internalHref); return; }
    window.location.assign(finalBookingUrl);
  };
  const scheduleRedirect = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => goBooking('dialog_auto'), REDIRECT_MS);
  };

  const submitContact = async (e) => {
    e?.preventDefault();
    setTouched(true);
    if (!valid || sending) return;
    const key = `${name.trim()}|${email.trim().toLowerCase()}|${phone.trim()}`;
    if (leadId && submittedKey === key) { setStep(1); return; } // già inviato con gli stessi dati (back → avanti)
    setSending(true);
    try {
      const r = await api.post('/public/funnel-lead', {
        funnel,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        property_slug: propertySlug || null,
        message: leadMessage || null,
        attribution: getAttribution(),
        intent,
      });
      if (r.data?.lead_id) setLeadId(r.data.lead_id);
      setSubmittedKey(key);
      setStep(1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.toastErr);
    } finally {
      setSending(false);
    }
  };

  const finish = () => {
    setStep(2);
    toast.success(t.toastTitle, { description: brochureEmail ? t.toastBrochure : t.toast });
    scheduleRedirect();
  };

  const submitPrequal = async () => {
    if (sending) return;
    if (!pq) { finish(); return; }
    setSending(true);
    try {
      if (leadId) {
        await api.post('/public/funnel-lead/prequal', { lead_id: leadId, funnel, prequal: pq, attribution: getAttribution() });
      }
    } catch { /* la prequalifica è "best effort": non blocca la prenotazione */ }
    finally { setSending(false); }
    finish();
  };

  const skipPrequal = () => finish();

  const CSS = `
    .zld-field { background: #FFFFFF; border: 1px solid ${tokens.line}; color: ${tokens.ink}; caret-color: ${tokens.accent}; transition: border-color .2s, box-shadow .2s; }
    .zld-field::placeholder { color: #8B9088; }
    .zld-field:hover:not(:focus) { border-color: #C9C0AF; }
    .zld-field:focus { border-color: ${tokens.accent}; box-shadow: 0 0 0 3px ${tokens.accent}29; outline: none; }
    .zld-field[aria-invalid="true"] { border-color: #B4533F; }
    .zld-btn { background: ${tokens.accent}; color: ${tokens.onAccent}; box-shadow: 0 10px 24px ${tokens.accent}38; transition: transform .2s, box-shadow .2s, background-color .2s, opacity .2s; }
    .zld-btn:hover:not(:disabled) { background: ${tokens.accentDeep}; transform: translateY(-1px); }
    .zld-btn:active:not(:disabled) { transform: translateY(0); }
    .zld-btn:focus-visible { outline: 2px solid ${tokens.accent}; outline-offset: 3px; }
    .zld-btn:disabled { opacity: .5; cursor: not-allowed; }
    .zld-link { color: ${tokens.inkSoft}; text-decoration: underline; text-underline-offset: 3px; transition: color .15s; }
    .zld-link:hover, .zld-link:focus-visible { color: ${tokens.ink}; outline: none; }
    .zld-chip { background: #FFFFFF; color: ${tokens.ink}; border: 1px solid ${tokens.line}; transition: background-color .15s, border-color .15s, color .15s, transform .15s; }
    .zld-chip:hover { border-color: ${tokens.accent}; }
    .zld-chip:active { transform: scale(0.98); }
    .zld-chip:focus-visible { outline: 2px solid ${tokens.accent}; outline-offset: 2px; }
    .zld-chip[data-state="selected"] { background: ${tokens.accent}; border-color: ${tokens.accent}; color: ${tokens.onAccent}; }
    @media (prefers-reduced-motion: reduce) { .zld-btn, .zld-chip, .zld-field { transition: none; } }
  `;

  const steps = t.steps || ['Contatti', 'Profilo', 'Calendario'];
  const pqT = t.pq || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-2rem)] max-w-lg rounded-2xl p-0 overflow-hidden border-0 gap-0 max-h-[92vh] overflow-y-auto"
        style={{ background: tokens.bg, color: tokens.ink }}
        data-testid={`${testIdPrefix}-lead-dialog`}
        data-step={step + 1}
      >
        <style>{CSS}</style>
        <div className="h-1.5 w-full" style={{ background: tokens.accent }} />
        <div className="p-6 sm:p-7">
          <div className="mb-5 pr-8"><Stepper steps={steps} current={step} tokens={tokens} testIdPrefix={testIdPrefix} /></div>

          {/* ── STEP 1 · Contatti ── */}
          {step === 0 && (
            <form onSubmit={submitContact} className="space-y-3.5" data-testid={`${testIdPrefix}-lead-form`} noValidate>
              <DialogHeader className="space-y-2 text-left">
                <div className="inline-flex items-center gap-2 text-[11px] font-sans font-bold tracking-[0.22em] uppercase" style={{ color: tokens.accent }}>
                  <Video className="w-3.5 h-3.5" aria-hidden="true" /> {t.kicker}
                </div>
                <DialogTitle className="font-serif text-2xl sm:text-[1.7rem] leading-tight text-left" style={{ color: tokens.ink }}>
                  {t.title}
                </DialogTitle>
                <DialogDescription className="font-sans text-sm text-left" style={{ color: tokens.inkSoft }}>
                  {brochureEmail ? t.descBrochure : t.desc}
                </DialogDescription>
              </DialogHeader>
              <div>
                <Field icon={User} tokens={tokens} placeholder={t.phName} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" testId={`${testIdPrefix}-form-name`} aria-invalid={touched && !nameOk ? 'true' : undefined} aria-label={t.phName} />
                {touched && !nameOk && <p className="mt-1 text-[11.5px] font-sans" style={{ color: '#B4533F' }} data-testid={`${testIdPrefix}-form-name-error`}>{t.errName}</p>}
              </div>
              <div>
                <Field icon={Mail} tokens={tokens} type="email" placeholder={t.phEmail} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" testId={`${testIdPrefix}-form-email`} aria-invalid={touched && !emailOk ? 'true' : undefined} aria-label={t.phEmail} />
                {touched && !emailOk && <p className="mt-1 text-[11.5px] font-sans" style={{ color: '#B4533F' }} data-testid={`${testIdPrefix}-form-email-error`}>{t.errEmail}</p>}
              </div>
              <Field icon={Phone} tokens={tokens} type="tel" placeholder={t.phPhone} value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" testId={`${testIdPrefix}-form-phone`} aria-label={t.phPhone} />
              <button type="submit" disabled={sending} className="zld-btn w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 h-12 text-[13px] sm:text-sm font-sans font-semibold" data-testid={`${testIdPrefix}-form-submit`}>
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t.submit} <ArrowRight className="w-4 h-4" />
              </button>
              <NoObligationNote lang={lang} theme="light" compact testId={`${testIdPrefix}-form-no-obligation`} />
              <p className="text-[11px] font-sans text-center leading-relaxed" style={{ color: tokens.inkSoft }}>
                {t.legal}{' '}
                <Link to="/privacy" className="underline underline-offset-2" style={{ color: tokens.ink }} data-testid={`${testIdPrefix}-form-privacy`}>{t.privacy}</Link>.
              </p>
            </form>
          )}

          {/* ── STEP 2 · Prequalifica (prima di Calendly) ── */}
          {step === 1 && (
            <div className="space-y-5" data-testid={`${testIdPrefix}-prequal-step`}>
              <DialogHeader className="space-y-2 text-left">
                <div className="inline-flex items-center gap-2 text-[11px] font-sans font-bold tracking-[0.22em] uppercase" style={{ color: tokens.accent }}>
                  <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" /> {pqT.kicker}
                </div>
                <DialogTitle className="font-serif text-2xl sm:text-[1.7rem] leading-tight text-left" style={{ color: tokens.ink }}>
                  {pqT.title}
                </DialogTitle>
                <DialogDescription className="font-sans text-sm text-left" style={{ color: tokens.inkSoft }}>
                  {pqT.desc}
                </DialogDescription>
              </DialogHeader>
              <ChipGroup icon={Target} label={pqT.goal} options={PREQUAL_OPTIONS.goal} value={prequal.goal} onChange={(v) => setPrequal((p) => ({ ...p, goal: v }))} lang={lang} tokens={tokens} testId={`${testIdPrefix}-pq-goal`} />
              <ChipGroup icon={Wallet} label={pqT.budget} options={PREQUAL_OPTIONS.budget} value={prequal.budget_bracket} onChange={(v) => setPrequal((p) => ({ ...p, budget_bracket: v }))} lang={lang} tokens={tokens} testId={`${testIdPrefix}-pq-budget`} />
              <ChipGroup icon={CalendarRange} label={pqT.timeline} options={PREQUAL_OPTIONS.timeline} value={prequal.timeline} onChange={(v) => setPrequal((p) => ({ ...p, timeline: v }))} lang={lang} tokens={tokens} testId={`${testIdPrefix}-pq-timeline`} />
              <button type="button" onClick={submitPrequal} disabled={!pqComplete || sending} className="zld-btn w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 h-12 text-[13px] sm:text-sm font-sans font-semibold" data-testid={`${testIdPrefix}-pq-submit`}>
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} {pqT.submit} <ArrowRight className="w-4 h-4" />
              </button>
              <div className="flex items-center justify-between gap-3 text-[12px] font-sans">
                <button type="button" onClick={() => setStep(0)} className="zld-link inline-flex items-center gap-1" data-testid={`${testIdPrefix}-pq-back`}>
                  <ArrowLeft className="w-3.5 h-3.5" /> {pqT.back}
                </button>
                <button type="button" onClick={skipPrequal} className="zld-link" data-testid={`${testIdPrefix}-pq-skip`}>{pqT.skip}</button>
              </div>
              <p className="text-[11px] font-sans leading-relaxed" style={{ color: tokens.inkSoft }}>{pqT.note}</p>
            </div>
          )}

          {/* ── STEP 3 · Conferma + redirect Calendly ── */}
          {step === 2 && (
            <div className="text-center py-1" data-testid={`${testIdPrefix}-form-success`}>
              <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: tokens.accent }} strokeWidth={1.4} />
              <DialogHeader className="mt-4 space-y-2">
                <DialogTitle className="font-serif text-2xl text-center" style={{ color: tokens.ink }}>{t.okTitle}</DialogTitle>
                <DialogDescription className="font-sans text-sm text-center" style={{ color: tokens.inkSoft }}>
                  {brochureEmail ? t.okBrochure : t.ok}
                </DialogDescription>
              </DialogHeader>
              <a
                href={finalBookingUrl}
                onClick={(e) => { e.preventDefault(); goBooking('dialog_cta'); }}
                className="zld-btn mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 h-12 text-[13px] sm:text-sm font-sans font-semibold"
                data-testid={`${testIdPrefix}-success-booking`}
              >
                <CalendarClock className="w-4 h-4" /> {t.okCta} <ArrowRight className="w-4 h-4" />
              </a>
              <p className="mt-3 inline-flex items-center justify-center gap-2 w-full text-[12px] font-sans" style={{ color: tokens.inkSoft }} data-testid={`${testIdPrefix}-success-note`}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t.okNote}
              </p>
              {bridge && (
                <div className="mt-5 rounded-xl px-4 py-3.5 text-left flex items-start gap-3" style={{ background: tokens.soft }} data-testid={`${testIdPrefix}-success-bridge`}>
                  <MapPin className="w-5 h-5 shrink-0 mt-0.5" style={{ color: tokens.accent }} strokeWidth={1.8} aria-hidden="true" />
                  <div className="min-w-0">
                    <div className="font-serif text-[15px] leading-tight" style={{ color: tokens.ink }}>{bridge.title}</div>
                    <p className="mt-1 text-[12.5px] font-sans leading-relaxed" style={{ color: tokens.inkSoft }}>{bridge.text}</p>
                    {bridge.href && bridge.cta && (
                      <Link to={bridge.href} className="zld-link mt-1.5 inline-flex items-center gap-1 text-[12.5px] font-sans font-semibold" data-testid={`${testIdPrefix}-success-bridge-link`}>
                        {bridge.cta} <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    )}
                  </div>
                </div>
              )}
              <NoObligationNote lang={lang} theme="light" compact className="mt-4" testId={`${testIdPrefix}-success-no-obligation`} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
